import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { Icon } from '../../../Atoms/Icon';
import { formatPercent, formatPrice, getDate, getFormattedDate } from '../../../../utils/helpers';
import { RecordLink } from '../../../Atoms/RecordLink';

const DefaultDefs: GridSingleSelectColDef[] = [
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'commissionsValidatedDate',
    headerName: 'Commissions Validation Date',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false,
    minWidth: 150,
    flex: 1,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM DD YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    description: 'Drag to move column'
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'commissionsValidationStatus',
    headerName: 'Commissions Validation Status',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column'
  },
  {
    field: 'commissionStatus',
    headerName: 'Commission Status',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 150,
    type: 'singleSelect'
  },
  {
    field: 'salesAgentName',
    headerName: 'Sales Agent',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 200,
    type: 'singleSelect',
    valueGetter: (value, row) => row.salesAgentName || row.assignedAgencyGroup
  },
  {
    field: 'accountName',
    headerName: 'Customer',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 200,
    type: 'singleSelect',
    valueGetter: (value, row) => row.accountName || row.sfdcCustomerName
  },
  {
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    minWidth: 200,
    flex: 2,
    type: 'singleSelect',
    valueGetter: (value, row) => row.supplierName || row.supplier
  },
  {
    field: 'tsdName',
    headerName: 'TSD',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 200,
    type: 'singleSelect',
    valueGetter: (value, row) => row.tsdName || row.tsd
  },
  {
    field: 'productName',
    headerName: 'Product',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 180,
    type: 'singleSelect',
    valueGetter: (value, row) => row.productName || row.rpmProductName
  },
  {
    field: 'mrc',
    headerName: 'Total MRC',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 100,
    type: 'singleSelect',
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value ? Number(params.value) : undefined)}
      </span>
    )
  },
  {
    field: 'commissionableMrc',
    headerName: 'Commissionable MRC',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 150,
    type: 'singleSelect',
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value ? Number(params.value) : undefined)}
      </span>
    )
  },
  {
    field: 'recurringCommission',
    headerName: 'Recurring Commission %',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 170,
    type: 'singleSelect',
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {params.value ? formatPercent(Number(params.value)) : undefined}
      </span>
    )
  },
  {
    field: 'expectedGrossMrr',
    headerName: 'Gross MRR',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect',
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value ? Number(params.value) : undefined)}
      </span>
    )
  },
  {
    field: 'actualFirstCommissionRun',
    headerName: 'Actual First Commission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 185,
    type: 'singleSelect'
  },
  {
    field: 'contractStartDate',
    headerName: 'Contract Sign Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 150,
    type: 'singleSelect'
  },
  {
    field: 'contractTerm',
    headerName: 'Term',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 100,
    type: 'singleSelect'
  },
  {
    field: 'circuitId',
    headerName: 'Circuit ID',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 150,
    type: 'singleSelect'
  },
  {
    field: 'location',
    headerName: 'Location',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 150,
    type: 'singleSelect',
    valueGetter: (value, row) => row.location || row.address
  },
  {
    field: 'tsdTrackingNumber',
    headerName: 'TSD Tracking Number',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 150,
    type: 'singleSelect',
    valueGetter: (value, row) => row.tsdTrackingNumber || row.tsdOrderId
  },
  {
    field: 'cancelledDate',
    headerName: 'Cancelled Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect'
  },
  {
    field: 'disconnectDate',
    headerName: 'Disconnect Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect'
  },
  {
    field: 'commissionableNrc',
    headerName: 'Commissionable NRC',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 150,
    type: 'singleSelect',
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value ? Number(params.value) : undefined)}
      </span>
    )
  },
  {
    field: 'nonRecurringCommission',
    headerName: 'Non Recurring Commission %',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 200,
    type: 'singleSelect',
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {params.value ? formatPercent(Number(params.value)) : undefined}
      </span>
    )
  },
  {
    field: 'provisionalType',
    headerName: 'Provisional Type',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 150,
    type: 'singleSelect'
  }
];

const PreviewDefs: GridSingleSelectColDef[] = [
  {
    field: 'rpmAccountId',
    headerName: 'RPM Account ID',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect'
  },
  {
    field: 'rpmAccountNumber',
    headerName: 'RPM Account',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect'
  },
  ...DefaultDefs
];

const CustomerErrorsDefs: GridSingleSelectColDef[] = [
  {
    headerName: 'RPM Customer',
    field: 'accountCustomerName',
    flex: 2,
    type: 'singleSelect',
    minWidth: 150,
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    renderCell: (params: GridCellParams) => (
      <a
        className="text-indigo"
        title="Open in RPM"
        href={`${process.env.REACT_APP_RPM_URL}/rpm/Page/CustomerAccount.aspx?Item=${params.row.accountCustomerId}`}
        target="_blank"
        rel="noopener noreferrer">
        <div className="flex items-center text-small">
          {params.row.accountCustomerName}
          <Icon type="external" />
        </div>
      </a>
    )
  },
  {
    field: 'accountCustomerId',
    headerName: 'RPM Customer Id',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 150,
    type: 'singleSelect'
  },
  {
    field: 'sfdcCustomerName',
    headerName: 'Salesforce Customer',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 150,
    type: 'singleSelect'
  }
];

const ProductErrorsDefs: GridSingleSelectColDef[] = [
  {
    field: 'supplier',
    headerName: 'RPM Supplier',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 150,
    type: 'singleSelect'
  },
  {
    headerName: 'RPM Product',
    field: 'rpmProductName',
    flex: 2,
    type: 'singleSelect',
    minWidth: 200,
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    renderCell: (params: GridCellParams) => (
      <a
        className="text-indigo"
        title="Open in RPM"
        href={`${process.env.REACT_APP_RPM_URL}/rpm/Page/Product.aspx?Item=${params.row.productId}`}
        target="_blank"
        rel="noopener noreferrer">
        <div className="flex items-center text-small">
          {params.row.rpmProductName}
          <Icon type="external" />
        </div>
      </a>
    )
  },
  {
    field: 'sfdcProductFamily',
    headerName: 'Salesforce Product Family',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 170,
    type: 'singleSelect'
  },
  {
    field: 'salesforceProductName',
    headerName: 'Salesforce Product Name',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 2,
    minWidth: 170,
    type: 'singleSelect'
  }
];

const OtherErrorsDefs: GridSingleSelectColDef[] = [
  {
    field: 'error',
    headerName: 'Error',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 5,
    minWidth: 300,
    type: 'singleSelect'
  },
  {
    field: 'rpmAccountId',
    headerName: 'RPM Account ID',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect'
  },
  ...DefaultDefs
];

const SuccessDefs: GridSingleSelectColDef[] = [
  {
    field: 'assetNumber',
    headerName: 'Number',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect',
    renderCell: (params: GridCellParams) => (
      <RecordLink to={`/provisional-asset/${params.row.id}`}>
        <div className="flex items-center text-small">{params.row.assetNumber}</div>
      </RecordLink>
    )
  },
  ...DefaultDefs
];

const FailedDefs: GridSingleSelectColDef[] = [
  {
    field: 'error',
    headerName: 'Error',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 5,
    minWidth: 300,
    type: 'singleSelect'
  },
  {
    field: 'rpmAccountId',
    headerName: 'RPM Account ID',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    flex: 1,
    minWidth: 120,
    type: 'singleSelect',
    valueGetter: (value, row) => row.rpmAccountId || row.accountId || '',
    renderCell: (params: GridCellParams) => {
      if (params.value) {
        return (
          <RecordLink to={`/rpm-account/${params.value}`}>
            <div className="flex items-center text-small">{params.value as string}</div>
          </RecordLink>
        );
      }
      return <span>-</span>;
    }
  },
  ...DefaultDefs
];

export const getColumnDefsByKey = (key: string): GridSingleSelectColDef[] => {
  switch (key) {
    case 'preview':
      return PreviewDefs;
    case 'customerErrors':
      return CustomerErrorsDefs;
    case 'productErrors':
      return ProductErrorsDefs;
    case 'otherErrors':
      return OtherErrorsDefs;
    case 'success':
      return SuccessDefs;
    case 'failed':
      return FailedDefs;
    default:
      return DefaultDefs;
  }
};
